import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale, hrLocale } from 'ngx-bootstrap/chronos';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { Player, SupportedLanguage } from '../models/player';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  public static readonly FALLBACK_LANGUAGE: SupportedLanguage = 'en';
  public static readonly SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'hr'];
  private static readonly PUBLIC_CACHE_KEY = 'nogosh.locale.public';

  private readonly localeSubject = new BehaviorSubject<SupportedLanguage>(LocaleService.FALLBACK_LANGUAGE);
  public readonly locale$ = this.localeSubject.asObservable();

  constructor(private translate: TranslateService, private calendarLocale: BsLocaleService) {
    defineLocale('hr', hrLocale);
    this.translate.addLangs(LocaleService.SUPPORTED_LANGUAGES);
    this.translate.setDefaultLang(LocaleService.FALLBACK_LANGUAGE);
    this.translate.use(LocaleService.FALLBACK_LANGUAGE).subscribe({
      error: (error) => console.warn('Unable to load English fallback translations.', error)
    });
  }

  public initialize(player: Player): Observable<SupportedLanguage> {
    const language = this.normalize(player && player.preferredLanguage);
    return this.use(language, player && player.id).pipe(
      catchError((error) => {
        if (language !== LocaleService.FALLBACK_LANGUAGE) {
          console.warn(`Unable to load translations for ${language}; falling back to English.`, error);
          return this.use(LocaleService.FALLBACK_LANGUAGE, player && player.id);
        }
        throw error;
      })
    );
  }

  public use(language: SupportedLanguage, playerId?: string): Observable<SupportedLanguage> {
    const normalizedLanguage = this.normalize(language);
    return this.translate.use(normalizedLanguage).pipe(
      tap(() => {
        this.localeSubject.next(normalizedLanguage);
        this.calendarLocale.use(normalizedLanguage);
        document.documentElement.lang = normalizedLanguage === 'hr' ? 'hr' : 'en';
        if (playerId) {
          localStorage.setItem(this.getCacheKey(playerId), normalizedLanguage);
        }
      }),
      map(() => normalizedLanguage)
    );
  }

  public usePublic(language: SupportedLanguage): Observable<SupportedLanguage> {
    return this.use(language).pipe(
      tap((normalizedLanguage) => localStorage.setItem(LocaleService.PUBLIC_CACHE_KEY, normalizedLanguage))
    );
  }

  public hasCachedLanguage(): boolean {
    return !!this.cachedLanguage();
  }

  public cachedPublicLanguage(): SupportedLanguage | '' {
    try {
      return this.readSupportedCachedLanguage(LocaleService.PUBLIC_CACHE_KEY);
    } catch (_) {
      return '';
    }
  }

  public cachedLanguage(): SupportedLanguage | '' {
    try {
      const publicLanguage = this.cachedPublicLanguage();
      if (publicLanguage) {
        return publicLanguage;
      }

      const localeKey = Object.keys(localStorage)
        .find((key) => key.indexOf('nogosh.locale.') === 0);
      return localeKey ? this.readSupportedCachedLanguage(localeKey) : '';
    } catch (_) {
      return '';
    }
  }

  public get currentLanguage(): SupportedLanguage {
    return this.localeSubject.value;
  }

  public normalize(language: string): SupportedLanguage {
    if ((LocaleService.SUPPORTED_LANGUAGES as string[]).includes(language)) {
      return language as SupportedLanguage;
    }
    return LocaleService.FALLBACK_LANGUAGE;
  }

  public clearUserCache(playerId?: string): void {
    if (playerId) {
      localStorage.removeItem(this.getCacheKey(playerId));
    }
    localStorage.removeItem(LocaleService.PUBLIC_CACHE_KEY);
    this.localeSubject.next(LocaleService.FALLBACK_LANGUAGE);
    this.calendarLocale.use(LocaleService.FALLBACK_LANGUAGE);
    this.translate.use(LocaleService.FALLBACK_LANGUAGE).subscribe({
      error: (error) => console.warn('Unable to restore English fallback translations.', error)
    });
    document.documentElement.lang = LocaleService.FALLBACK_LANGUAGE;
  }

  private getCacheKey(playerId: string): string {
    return `nogosh.locale.${playerId}`;
  }

  private readSupportedCachedLanguage(cacheKey: string): SupportedLanguage | '' {
    const cachedLanguage = localStorage.getItem(cacheKey);
    return (LocaleService.SUPPORTED_LANGUAGES as string[]).includes(cachedLanguage)
      ? cachedLanguage as SupportedLanguage
      : '';
  }
}
